import {
  BrandVariants,
  Theme,
  createLightTheme,
  createDarkTheme,
  ColorPaletteTokens,
  ColorTokens,
  tokens,
} from '@fluentui/react-components';
import { ExtendedTokens, ExtendedTheme } from './types';
import { shared } from './shared';
import { AdsUITokens, InternalTokens, TypographyTokens } from '../tokens';
import { generateFontToken } from '../utils';

// ///////////////////////////////////
//
// Compact theme
//
// ///////////////////////////////////

export const themeLabel = 'Compact';

// override brand tokens for current theme
export const brandVariants: BrandVariants = {
  10: `#061724`,
  20: `#082338`,
  30: `#0a2e4a`,
  40: `#0c3b5e`,
  50: `#0e4775`,
  60: `#0f548c`,
  70: `#115ea3`,
  80: `#0f6cbd`,
  90: `#2886de`,
  100: `#479ef5`,
  110: `#62abf5`,
  120: `#77b7f7`,
  130: `#96c6fa`,
  140: `#b4d6fa`,
  150: `#cfe4fa`,
  160: `#ebf3fc`,
};

const baseThemeLight = createLightTheme(brandVariants);
const baseThemeDark = createDarkTheme(brandVariants);

const internalTokens: InternalTokens = {
  fontSizeBase100: '10px',
  fontSizeBase200: '10px',
  fontSizeBase300: '12px',
  fontSizeBase400: '14px',
  fontSizeBase500: '16px',
  fontSizeBase600: '20px',
  fontSizeBase700: '24px',
  fontSizeBase800: '28px',
  fontSizeBase900: '32px',
  fontSizeBase1000: '40px',

  lineHeightBase100: '14px',
  lineHeightBase200: '14px',
  lineHeightBase300: '16px',
  lineHeightBase400: '20px',
  lineHeightBase500: '22px',
  lineHeightBase600: '28px',
  lineHeightBase700: '32px',
  lineHeightBase800: '36px',
  lineHeightBase900: '40px',
  lineHeightBase1000: '52px',

  spacingXXXL: '24px',
  spacingXXL: '20px',
  spacingXL: '16px',
  spacingL: '12px',
  spacingM: '10px',
  spacingMNudge: '8px',
  spacingS: '6px',
  spacingSNudge: '4px',
  spacingXS: '2px',
  spacingXXS: '2px',
};

const typographyTokens: TypographyTokens = {
  // Line Height
  adsui_typographyLineHeightBase100: internalTokens.lineHeightBase100,
  adsui_typographyLineHeightBase200: internalTokens.lineHeightBase100,
  adsui_typographyLineHeightBase300: internalTokens.lineHeightBase200,
  adsui_typographyLineHeightBase400: internalTokens.lineHeightBase300,
  adsui_typographyLineHeightBase500: internalTokens.lineHeightBase400,
  adsui_typographyLineHeightBase600: internalTokens.lineHeightBase500,
  adsui_typographyLineHeightBase700: internalTokens.lineHeightBase600,
  adsui_typographyLineHeightBase800: internalTokens.lineHeightBase700,
  adsui_typographyLineHeightBase900: internalTokens.lineHeightBase800,
  adsui_typographyLineHeightBase1000: internalTokens.lineHeightBase900,

  // Font Size
  adsui_typographyFontSizeBase100: internalTokens.fontSizeBase100,
  adsui_typographyFontSizeBase200: internalTokens.fontSizeBase100,
  adsui_typographyFontSizeBase300: internalTokens.fontSizeBase200,
  adsui_typographyFontSizeBase400: internalTokens.fontSizeBase300,
  adsui_typographyFontSizeBase500: internalTokens.fontSizeBase400,
  adsui_typographyFontSizeBase600: internalTokens.fontSizeBase500,
  adsui_typographyFontSizeBase700: internalTokens.fontSizeBase600,
  adsui_typographyFontSizeBase800: internalTokens.fontSizeBase700,
  adsui_typographyFontSizeBase900: internalTokens.fontSizeBase800,
  adsui_typographyFontSizeBase1000: internalTokens.fontSizeBase900,
};

const modeIndependentTokens: Partial<ExtendedTokens<AdsUITokens>> = {
  // Global Colors
  adsui_colorBackgroundDefault: tokens.colorNeutralBackgroundAlpha,
  adsui_colorBackgroundFilled: tokens.colorNeutralBackground2,
  adsui_colorBackgroundElevated: tokens.colorNeutralBackground1,

  // Radius
  adsui_radiusSmall: tokens.borderRadiusNone,
  adsui_radiusMedium: tokens.borderRadiusSmall,
  adsui_radiusLarge: tokens.borderRadiusMedium,
  adsui_radiusXLarge: tokens.borderRadiusLarge,

  // Spacing
  adsui_spacingXXS: internalTokens.spacingXXS,
  adsui_spacingXS: internalTokens.spacingXXS,
  adsui_spacingSNudge: internalTokens.spacingXS,
  adsui_spacingS: internalTokens.spacingSNudge,
  adsui_spacingMNudge: internalTokens.spacingS,
  adsui_spacingM: internalTokens.spacingMNudge,
  adsui_spacingL: internalTokens.spacingM,
  adsui_spacingXL: internalTokens.spacingL,
  adsui_spacingXXL: internalTokens.spacingXL,
  adsui_spacingXXXL: internalTokens.spacingXXL,

  adsui_typographyCaption2: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase100,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase100,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption2Strong: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase100,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase100,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption1: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption1Strong: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption1Stronger: generateFontToken({
    fontWeight: tokens.fontWeightBold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody1: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody1Strong: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody1Stronger: generateFontToken({
    fontWeight: tokens.fontWeightBold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody2: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySubtitle2: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySubtitle2Stronger: generateFontToken({
    fontWeight: tokens.fontWeightBold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySubtitle1: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase500,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase500,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyTitle3: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase600,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase600,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyTitle2: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase700,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase700,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyTitle1: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase800,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase800,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyLargeTitle: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase900,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase900,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyDisplay: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase1000,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase1000,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyPageTitle: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase600,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase600,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyPageSubheader: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySectionTitle: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySectionSubheader: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
};

// override component tokens for current theme
const adsUITokensLight: ExtendedTokens<AdsUITokens> = {
  ...shared.componentTokensLight,
  ...modeIndependentTokens,
};

const adsUITokensDark: ExtendedTokens<AdsUITokens> = {
  ...shared.componentTokensDark,
  ...modeIndependentTokens,
};

// override base fluent theme tokens for light theme
const colorTokensLight: ExtendedTokens<ColorTokens> = {
  ...baseThemeLight,
  ...shared.colorTokensLight,
};

// override tokens for Dark theme
const colorTokensDark: ExtendedTokens<ColorTokens> = {
  ...baseThemeDark,
  ...shared.colorTokensDark,
};

// override base fluent theme pallete tokens for light and dark theme
const colorPalletTokensLight: ExtendedTokens<ColorPaletteTokens> = {
  ...baseThemeLight,
};

const colorPalletTokensDark: ExtendedTokens<ColorPaletteTokens> = {
  ...baseThemeDark,
};

export const themeLight = {
  ...colorTokensLight,
  ...colorPalletTokensLight,
  ...adsUITokensLight,
} as ExtendedTheme<Theme>;

export const themeDark = {
  ...colorTokensDark,
  ...colorPalletTokensDark,
  ...adsUITokensDark,
} as ExtendedTheme<Theme>;
